import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { auth } from '../firebase/config';
import { sendVerificationEmail, updateEmailVerificationStatus } from '../services/authService';
import { getTransactions, Transaction, calculateStoreCredit, getBusinessesWithCredit } from '../services/transactionService';
import { adminService } from '../services/adminService';
import { useAuth } from '../contexts/AuthContext';
import TopBar from '../components/TopBar';
import Button from '../components/Button';
import storeCreditIcon from '../assets/store-credit-icon.png';

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const VerificationAlert = styled.div`
  background-color: #fff3e0;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  width: 100%;
`;

const AlertTitle = styled.h3`
  font-size: 18px;
  color: #e65100;
  margin-bottom: 10px;
`;

const AlertText = styled.p`
  font-size: 14px;
  color: #333;
  margin-bottom: 15px;
`;

const ResendButton = styled.button`
  background-color: #ff9800;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: #f57c00;
  }
`;

const SuccessMessage = styled.p`
  color: #43a047;
  margin-top: 10px;
  font-size: 14px;
`;

const ErrorMessage = styled.p`
  color: #e53935;
  margin-top: 10px;
  font-size: 14px;
`;

const MainContent = styled.div`
  margin-bottom: 40px;
  width: 100%;
  text-align: center;
`;

const WelcomeHeading = styled.h1`
  font-size: 38px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #333;
`;

const CreditSummary = styled.p`
  font-size: 18px;
  color: #555;
  margin-bottom: 24px;
  max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  strong {
    font-weight: 700;
  }
`;

const CreditCard = styled.div`
  background-color: #fff;
  border-radius: 100px;
  padding: 24px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  max-width: 390px;
  margin-left: auto;
  margin-right: auto;
  box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.03);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CreditIcon = styled.img`
  width: 42px;
  height: 42px;
`;

const CreditAmount = styled.h2`
  font-size: 3rem;
  font-weight: 900;
  color: #333;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 40px;
  width: 100%;
  max-width: 390px;
  margin-left: auto;
  margin-right: auto;
`;

const BusinessSection = styled.div`
  text-align: center;
  padding: 30px;
  border-top: 1px solid #e0e0e0;
  margin-top: 20px;
  width: 100%;
`;

const BusinessHeading = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 16px;
  color: #333;
`;

const BusinessText = styled.p`
  font-size: 16px;
  color: #555;
  margin-bottom: 24px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const DeleteAccountLink = styled.button`
  background: none;
  border: none;
  color: #999;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
  margin-top: 16px;
  font-family: 'Montserrat', sans-serif;
  display: block;
  width: 100%;
  text-align: center;
  &:hover {
    color: #666;
  }
`;

const DashboardScreen: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState('');
  const [resendError, setResendError] = useState('');
  const [hasCheckedProfilePicture, setHasCheckedProfilePicture] = useState(false);
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();

  const fetchUserData = async () => {
    try {
      setLoading(true);
      
      // Use user from AuthContext instead of calling getCurrentUser
      if (!user) {
        console.error('DashboardScreen - No user data available');
        setLoading(false);
        return;
      }
      
      console.log('DashboardScreen - user from AuthContext:', user);
      console.log('DashboardScreen - user.firstName:', user.firstName);
      console.log('DashboardScreen - user.lastName:', user.lastName);
      
      // Clear URL parameters if they exist
      const urlParams = new URLSearchParams(window.location.search);
      const profileUpdated = urlParams.get('profileUpdated');
      const skipped = urlParams.get('skippedProfilePicture');
      
      if (profileUpdated || skipped) {
        console.log('Clearing URL parameters...');
        window.history.replaceState({}, '', window.location.pathname);
      }
      
      // Check if user needs to upload profile picture - only if we have complete user data
      // and haven't just come from an update or already checked
      if (user.firstName && 
          user.lastName && 
          (!user.profilePictureURL || user.profilePictureURL === '') && 
          !skipped && 
          !profileUpdated &&
          !hasCheckedProfilePicture) {
        console.log('Redirecting to profile picture upload...');
        setHasCheckedProfilePicture(true);
        navigate('/upload-profile-picture', { 
          state: { 
            required: false, 
            fromRegistration: false 
          } 
        });
        return;
      }
      
      if (user.id) {
        const userTransactions = await getTransactions(user.id);
        setTransactions(userTransactions);
        console.log('Transactions:', userTransactions);
      } else {
        console.error('User ID not found');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch data when auth is not loading and we have a user
    if (!authLoading && user) {
      fetchUserData();
    }
  }, [user?.id, authLoading]); // Only depend on user ID to prevent unnecessary re-renders

  // Reset profile picture check flag when user changes
  useEffect(() => {
    setHasCheckedProfilePicture(false);
  }, [user?.id]);

  // Handle navigation to login when user is not available
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendSuccess('');
    setResendError('');
    
    try {
      await sendVerificationEmail();
      setResendSuccess('Verification email sent successfully! Please check your inbox.');
    } catch (error) {
      console.error('Error sending verification email:', error);
      setResendError('Failed to send verification email. Please try again later.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    try {
      await updateEmailVerificationStatus();
      await fetchUserData();
    } catch (error) {
      console.error('Error checking verification status:', error);
    }
  };
  
  const handleGetApp = () => {
    // Open a modal or redirect to app download page
    const userAgent = navigator.userAgent || navigator.vendor;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      
      if (isIOS) {
        window.open('https://apps.apple.com/us/app/store-credit/id6746509517', '_blank');
      } else {
        window.open('https://play.google.com/store/apps/details?id=com.brownvincicode24.storecreditappc', '_blank');
      }
  };
  
  const handleMyProfile = () => {
    navigate('/profile');
  };
  const handleChangePassword = () => {
    navigate('/change-password');
  };
  const handleMyBusinesses = () => {
    navigate('/businesses');
  };
    const handleRegisterBusiness = () => {
    navigate('/businesses/new');
  };
  const handleDeleteAccount = () => {
    navigate('/delete-account');
  };

  const handleAdminDashboard = () => {
    navigate('/admin');
  };

  // Debug: Log user data to see what's available
  useEffect(() => {
    if (user) {
      console.log('DashboardScreen - Current user data:', {
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        profilePictureURL: user.profilePictureURL,
        emailVerified: user.emailVerified,
        isVerified: user.isVerified
      });
    }
  }, [user]);

  // Show loading state while auth is initializing or data is being fetched
  if (authLoading || loading) {
    return (
      <>
        <Helmet>
          <title>{APP_NAME} - Dashboard</title>
          <meta name="description" content={`${APP_NAME} - Dashboard`} />
        </Helmet>
        <Container>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ fontFamily: 'Montserrat, sans-serif', color: '#666' }}>
              Loading your dashboard...
            </p>
          </div>
        </Container>
      </>
    );
  }

  // Check if user is not available after loading
  if (!user) {
    return (
      <>
        <Helmet>
          <title>{APP_NAME} - Dashboard</title>
          <meta name="description" content={`${APP_NAME} - Dashboard`} />
        </Helmet>
        <Container>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ fontFamily: 'Montserrat, sans-serif', color: '#666' }}>
              Redirecting to login...
            </p>
          </div>
        </Container>
      </>
    );
  }

  // Calculate credit information
  const totalCredit = calculateStoreCredit(transactions);
  const businessesWithCredit = getBusinessesWithCredit(transactions);
  const placesCount = businessesWithCredit.length;

  // Check if user is a manager or staff
  const isBusinessUser = user.role === 'manager' || user.role === 'staff';

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Dashboard</title>
        <meta name="description" content={`${APP_NAME} - Dashboard`} />
      </Helmet>
      <Container>
      <ContentWrapper>
        <TopBar user={user as any} />
        
        {!user.emailVerified && (
          <VerificationAlert>
            <AlertTitle>Email Verification Required</AlertTitle>
            <AlertText>
              Please verify your email address to access all features. 
              We've sent a verification link to {user.email}.
            </AlertText>
            <ResendButton 
              onClick={handleResendVerification}
              disabled={resendLoading}
            >
              {resendLoading ? 'Sending...' : 'Resend Verification Email'}
            </ResendButton>
            {' '}
            <ResendButton 
              onClick={handleCheckVerification}
            >
              I've Verified My Email
            </ResendButton>
            
            {resendSuccess && <SuccessMessage>{resendSuccess}</SuccessMessage>}
            {resendError && <ErrorMessage>{resendError}</ErrorMessage>}
          </VerificationAlert>
        )}
        
        <MainContent>
          <WelcomeHeading>Hi {user.firstName}!</WelcomeHeading>
          <CreditSummary>
            {isBusinessUser ? (
              <>You currently have ${totalCredit.toFixed(2)} in Store Credit at <strong>{placesCount} {placesCount === 1 ? 'place' : 'places'}</strong>. Use our mobile app to collect & redeem store credit.</>
            ) : (
              <>You currently have ${totalCredit.toFixed(2)} in Store Credit at <strong>{placesCount} {placesCount === 1 ? 'place' : 'places'}</strong>.</>
            )}
          </CreditSummary>
          
          <CreditCard>
            <CreditAmount>${totalCredit.toFixed(2)}</CreditAmount>
          </CreditCard>
          
          <ButtonContainer>
            <Button 
              text="Get The App" 
              onClick={handleGetApp} 
              bgColor="#000" 
              textColor="#fff"
              width="390px"
            />
            
            {isBusinessUser && (
              <Button 
                text="My Businesses" 
                onClick={handleMyBusinesses} 
                outline
                bgColor="#000" 
                textColor="#000"
                width="390px"
              />
            )}
            
            <Button 
              text="My Profile" 
              onClick={handleMyProfile} 
              outline
              bgColor="#000" 
              textColor="#000"
              width="390px"
            />            <Button 
              text="Change Password" 
              onClick={handleChangePassword} 
              outline
              bgColor="#000" 
              textColor="#000"
              width="390px"
            />

            {adminService.isAdmin(user as any) && (
              <Button 
                text="Admin Dashboard" 
                onClick={handleAdminDashboard} 
                outline
                bgColor="#381f74" 
                textColor="#381f74"
                width="390px"
              />
            )}
          </ButtonContainer>
        </MainContent>
          {!isBusinessUser && (
          <BusinessSection>
            <BusinessHeading>Own A Business?</BusinessHeading>
            <BusinessText>
              Register your business for free to start rewarding your loyal
              customers with store credit.
            </BusinessText>
            <ButtonContainer>
            <Button 
              text="Add" 
              onClick={handleRegisterBusiness} 
              outline
              bgColor="#000" 
              textColor="#000"
              width="390px"
            />
            </ButtonContainer>

          </BusinessSection>
        )}
            <DeleteAccountLink onClick={handleDeleteAccount}>
              Delete Account        </DeleteAccountLink>
      </ContentWrapper>
    </Container>
    </>
  );
};

export default DashboardScreen;